var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CameraProjection, WorldObjectType, } from "../../api";
import { ObjectFactory } from "../ObjectFactory";
import { ThreeRenderer } from "../../private/renderer";
import { AmbientLight, Matrix4, Raycaster, Scene, Vector2, Vector3, Vector4 } from "three";
import { FullscreenQuadCopyPass, ScenePass, TileCompositorPass } from "../../private/renderer/passes";
import { DebugDraw } from "../../private/renderer/debug";
import { World } from "../World";
import { Ellipsoid, MathUtils } from "../../private/math";
import { CameraAwareSunLight } from "../../private/objects/camera_aware_sun_light";
import { CesiumIon } from "../../private/data/cesium";
import { ClippingGroup } from "../ClippingGroup";
import { LineMesh } from "../LineMesh";
import { PointMesh } from "../PointMesh";
import { LineStripMesh } from "../LineStripMesh";
import { IndexedTriangleMesh } from "../IndexedTriangleMesh";
import { CameraObject } from "../CameraObject";
import { ObjectType, PickingManager } from "../../private/picking";
import { HarfBuzz } from "../../private/external/HarfBuzz";
import { FontLoader } from "../../private/data/loaders/FontLoader";
import { FontAsset } from "../FontAsset";
import { DracoLoader, FileLoader, GltfLoader, IfcLoader } from "../../private/data/loaders";
import { IfcObject, MeshObject } from "../objects";
import { SpriteObject } from "../objects/SpriteObject";
import { RequestManager } from "../../private/data/RequestManager";
import { InstancedMeshObject } from "../objects/InstancedMeshObject";
import { RenderTargets } from "./RenderTargets";
import { PickingCache } from "./PickingCache";
import { Google } from "../../private/data/google";
// noinspection PointlessArithmeticExpressionJS
/**
 * Implementation of IRenderer
 */
export class Renderer {
    constructor(desc) {
        this.lightingRigs = [
            new Scene(),
            new Scene(),
            new Scene(),
            new Scene(),
            new Scene(),
            new Scene(),
            new Scene(),
        ];
        this.pickingManager = new PickingManager();
        this.raycastCache = new Map();
        this.objectFactory = new ObjectFactory(this);
        this.world = new World(this);
        // Build the renderer based on the container dimensions
        const { width, height } = desc.container.getBoundingClientRect();
        this.camera = new CameraObject(75, width / height, 0.1, Ellipsoid.WGS_84.radii.length() * 30);
        const { threeRenderer, targets, passSets, tileCompositorPass } = createRenderer(width, height);
        FileLoader.CONFIGURATION = threeRenderer.config;
        this.requestManager = new RequestManager(threeRenderer.config.globeNetworkRequests, threeRenderer.config.tilesetNetworkRequests);
        // Add threeJS's canvas to the container
        desc.container.appendChild(threeRenderer.getDomElement());
        // Initialize cesium if requested
        let cesiumIon;
        if (desc.cesium !== undefined) {
            cesiumIon = new CesiumIon(desc.cesium.token, {});
        }
        let google;
        if (desc.google !== undefined) {
            google = new Google(desc.google.token, {});
        }
        // Provide the cesium and google accessors to the required components
        this.world.globe.cesium = cesiumIon;
        this.objectFactory.cesium = cesiumIon;
        this.objectFactory.google = google;
        this.threeRenderer = threeRenderer;
        this.targets = targets;
        this.passSets = passSets;
        this.tileCompositorPass = tileCompositorPass;
        for (const rig of this.lightingRigs) {
            rig.add(new AmbientLight(0xffffffff, 0.2));
            rig.add(new CameraAwareSunLight());
        }
        this.pickingCache = new PickingCache(this);
    }
    get config() {
        return this.threeRenderer.config;
    }
    static create(desc) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            // Initialize the HarfBuzz module so rendering code will be able to safely assume it is loaded
            yield HarfBuzz.init((_a = desc.hb) === null || _a === void 0 ? void 0 : _a.moduleUrl);
            // Initialize the IfcLoader module
            yield IfcLoader.init((_b = desc.ifc) === null || _b === void 0 ? void 0 : _b.moduleBaseUrl);
            // Pre-load the draco loader
            yield DracoLoader.get((_c = desc.draco) === null || _c === void 0 ? void 0 : _c.moduleBaseUrl);
            // Set the gltf loader with the correct draco URL
            GltfLoader.configureLoaders((_d = desc.draco) === null || _d === void 0 ? void 0 : _d.moduleBaseUrl);
            return new Renderer(desc);
        });
    }
    getObjectFactory() {
        return this.objectFactory;
    }
    getWorld() {
        return this.world;
    }
    render() {
        // Update the request manager, popping some requests off the queue if there's allowed capacity available.
        this.requestManager.update();
        // Swap the back buffer index to render with the next buffers in the chain
        this.targets.incrementBufferIndex();
        // Invalidate the picking and raycast caches now that we're rendering the next frame
        this.pickingCache.newFrame();
        this.raycastCache.clear();
        // Get this frame's scene from the world
        const scenes = this.world.buildScene();
        // Center the panorama scene on the camera
        scenes[3].position.copy(this.camera.object.position);
        // Add our lighting rig to the scene for rendering with
        for (let i = 0; i < scenes.length; i += 1) {
            scenes[i].add(this.lightingRigs[i]);
        }
        // Kick off rendering
        this.threeRenderer.render(scenes, this.passSets[this.targets.currentIndex], this.camera.object);
        this.threeRenderer.nextFrame();
    }
    dispose() {
        this.threeRenderer.renderer.dispose();
        this.threeRenderer.renderer.forceContextLoss();
        this.threeRenderer.renderer.domElement.remove();
    }
    pickScreenWorldSpace(point) {
        return this.pickingCache.pickScreenWorldSpace(point);
    }
    pickScreenViewSpace(point) {
        return this.pickingCache.pickScreenViewSpace(point);
    }
    pickScreenWorldSpaceSnapping(point) {
        return this.pickingCache.pickScreenWorldSpaceSnapping(point);
    }
    pickScreenViewSpaceSnapping(point) {
        return this.pickingCache.pickScreenViewSpaceSnapping(point);
    }
    raycastScreen(point, parameters) {
        const hash = JSON.stringify(Object.assign(Object.assign({}, point), parameters));
        const item = this.raycastCache.get(hash);
        if (item !== undefined) {
            return item;
        }
        else {
            const result = this.raycastScreenImpl(point, parameters);
            this.raycastCache.set(hash, result);
            return result;
        }
    }
    static handlePickResult(result) {
        if (result.object === null) {
            return {
                position: result.position,
                object: null,
            };
        }
        else {
            // We only pass the extraId through for objects that will contain a meaningful ID. Many objects don't use
            // the field, so we leave extraId undefined in those cases.
            let extraId;
            switch (result.object.rootObject.getObjectType()) {
                case WorldObjectType.PointObject: // These all provide user-provided ID via extraId
                case WorldObjectType.LineObject: // These all provide user-provided ID via extraId
                case WorldObjectType.LineSegmentObject: // These all provide user-provided ID via extraId
                case WorldObjectType.MeshObject: // These all provide user-provided ID via extraId
                case WorldObjectType.InstancedMesh: // Instanced mesh will encode the instanceId in the extraId field
                case WorldObjectType.IfcObject:
                    extraId = result.extraId;
                    break;
                case WorldObjectType.SpriteObject:
                    const object = result.object.rootObject;
                    extraId = object.pickingUserId;
                    break;
                case WorldObjectType.TilesetObject: // don't use the extraId field yet, but they may in the future
                case WorldObjectType.CameraObject: // Picking a camera object should be impossible
                case WorldObjectType.ObjObject: // OBJ objects don't use the extraId field, so we can ignore them
                case WorldObjectType.KmlObject: // KMLs don't use extraId
                case WorldObjectType.BoxObject: // Again no extraId
                case WorldObjectType.SphereObject: // No extraId as well
                case WorldObjectType.LabelCollection: // Labels don't use extraId
                    break;
            }
            switch (result.object.objectType) {
                case ObjectType.Primitive:
                    return {
                        position: result.position,
                        object: result.object.rootObject,
                        userData: extraId,
                    };
                case ObjectType.Composite:
                    return {
                        position: result.position,
                        object: result.object.rootObject,
                        userData: extraId,
                    };
            }
        }
    }
    raycastScreenImpl(point, parameters) {
        var _a, _b, _c;
        const _parameters = parameters !== null && parameters !== void 0 ? parameters : {};
        if (this.world.lastScene !== null) {
            const size = this.threeRenderer.getSize();
            const x = (point.x / size.x) * 2 - 1;
            const y = -(point.y / size.y) * 2 + 1;
            const caster = new Raycaster();
            caster.firstHitOnly = (_a = _parameters.firstHitOnly) !== null && _a !== void 0 ? _a : true;
            caster.params.Points = { threshold: (_b = _parameters.pointThreshold) !== null && _b !== void 0 ? _b : 0.01 };
            caster.params.Line = { threshold: (_c = _parameters.lineThreshold) !== null && _c !== void 0 ? _c : 0.01 };
            caster.setFromCamera(new Vector2(x, y), this.camera.object.getCurrentCamera());
            // Perform the intersection
            const intersections = caster.intersectObjects(this.world.lastScene.children, true);
            // Map the intersections into the result form, filtering out any non-pickable objects
            return this.mapRaycastIntersections(intersections);
        }
        else {
            return [];
        }
    }
    raycastScene(begin, end, parameters) {
        var _a, _b, _c;
        const _parameters = parameters !== null && parameters !== void 0 ? parameters : {};
        if (this.world.lastScene !== null) {
            // Convert the begin/end pair into the values needed by Raycaster
            const origin = new Vector3(begin.x, begin.y, begin.z);
            const direction = new Vector3(end.x, end.y, end.z).sub(origin);
            const caster = new Raycaster(origin, direction.clone().normalize(), 0, direction.length());
            caster.firstHitOnly = (_a = _parameters.firstHitOnly) !== null && _a !== void 0 ? _a : true;
            caster.params.Points = { threshold: (_b = _parameters.pointThreshold) !== null && _b !== void 0 ? _b : 0.01 };
            caster.params.Line = { threshold: (_c = _parameters.lineThreshold) !== null && _c !== void 0 ? _c : 0.01 };
            // Perform the intersection
            const intersections = caster.intersectObjects(this.world.lastScene.children, true);
            // Map the intersections into the result form, filtering out any non-pickable objects
            return this.mapRaycastIntersections(intersections);
        }
        else {
            return [];
        }
    }
    mapRaycastIntersections(intersections) {
        var _a, _b, _c;
        const mappedIntersections = intersections.flatMap((v) => {
            // Check from leaf to root, starting at the closest picked object, to find the first object in the
            // hierarchy with an associated picking id. If no object with a picking id is found in the tree walk
            // then we return as if we didn't intersect anything.
            let candidate = v.object;
            let id;
            while (true) {
                // Try to lookup a picking id for the candidate three id.
                id = this.pickingManager.getIdForThreeId(candidate.id);
                // If we found a picking id then break from the loop, we've found our object
                if (id !== undefined) {
                    break;
                }
                // If we didn't find a picking id for the threeJS object then we try the object's parent. If the
                // object has no parent then we haven't intersected any pick-able objects, so we break from the
                // loop by returning.
                if (candidate.parent === null) {
                    return [];
                }
                // We've proven the object *does* have a parent so we make the parent the next candidate and
                // continue looping.
                candidate = candidate.parent;
            }
            return [[v, id]];
        });
        const result = [];
        for (const [i, id] of mappedIntersections) {
            const object = (_b = (_a = this.pickingManager.getForId(id)) === null || _a === void 0 ? void 0 : _a.rootObject) !== null && _b !== void 0 ? _b : null;
            if (object === null) {
                continue;
            }
            const position = i.point.clone();
            let userData;
            if (object instanceof MeshObject) {
                if (object.mesh !== undefined && object.mesh.pickingUserId !== undefined) {
                    let idx;
                    if (object.mesh instanceof IndexedTriangleMesh && i.faceIndex !== undefined) {
                        idx = object.mesh.indices[i.faceIndex * 3];
                    }
                    else if (object.mesh instanceof PointMesh) {
                        idx = i.index;
                    }
                    else if (object.mesh instanceof LineStripMesh) {
                        idx = i.index;
                    }
                    else if (object.mesh instanceof LineMesh) {
                        idx = i.index;
                    }
                    if (idx === undefined)
                        throw new Error("No raycast->data mapping");
                    userData = object.mesh.pickingUserId[idx];
                }
            }
            if (object instanceof SpriteObject) {
                userData = object.pickingUserId;
            }
            if (object instanceof InstancedMeshObject) {
                userData = i.instanceId;
            }
            if (object instanceof IfcObject) {
                const mesh = i.object;
                const indices = mesh.geometry.index.array;
                const elementIds = mesh.geometry.getAttribute("pickingUserId");
                userData = elementIds.getX(indices[3 * ((_c = i.faceIndex) !== null && _c !== void 0 ? _c : -1)]);
            }
            result.push({
                object,
                position,
                userData,
            });
        }
        return result;
    }
    getCanvas() {
        return this.threeRenderer.getDomElement();
    }
    setSize(size) {
        this.threeRenderer.setSize(size.x, size.y);
        this.camera.setAspectRatio(size.x / size.y);
    }
    getSize() {
        return this.threeRenderer.getSize();
    }
    createClippingGroup() {
        return new ClippingGroup();
    }
    createIndexedTriangleMesh() {
        return new IndexedTriangleMesh();
    }
    createLineMesh() {
        return new LineMesh();
    }
    createLineStripMesh() {
        throw new LineStripMesh();
    }
    createPointMesh() {
        return new PointMesh();
    }
    /**
     * Performs a combined depth read-back, depth decode and unproject operation to get the world space position of what
     * ever is below the point x/y in screen coordinates.
     *
     * This can be used to get the position of the point below the mouse cursor
     * @param x Screen x coordinate to unproject
     * @param y Screen y coordinate to unproject
     * @param worldSpace Should the position be transformed into world space or be kept in view space. Default is world
     */
    pickScreen(x, y, worldSpace = true) {
        var _a;
        const size = this.threeRenderer.getSize();
        // Flip Y around center
        y = size.y / 2 - (y - size.y / 2);
        // Read the depth buffer and decode the depth based on the camera's projection
        const [rectX, rectY, rectW, rectH] = this.calculateReadPixelsArgs(x, y, 1, 1);
        const depthBuffer = this.readBackDepthBufferRect(rectX, rectY, rectW, rectH);
        const [objectIdBuffer, extraIdBuffer] = this.readBackPickingBufferRect(rectX, rectY, rectW, rectH);
        const position = this.decodeDepthToPosition(depthBuffer[0], rectX, rectY, worldSpace);
        const objectId = objectIdBuffer[0];
        const extraId = extraIdBuffer[0];
        const object = (_a = this.pickingManager.getForId(objectId)) !== null && _a !== void 0 ? _a : null;
        return {
            position,
            object,
            extraId,
        };
    }
    pickScreenSpiral(x, y, worldSpace = true) {
        var _a, _b;
        const size = this.threeRenderer.getSize();
        // Flip Y around center
        y = size.y / 2 - (y - size.y / 2);
        // Read the depth buffer and decode the depth based on the camera's projection
        const [rectX, rectY, rectW, rectH] = this.calculateReadPixelsArgs(x, y, SPIRAL_W, SPIRAL_H);
        const depthBuffer = this.readBackDepthBufferRect(rectX, rectY, rectW, rectH);
        const [objectIdBuffer, extraIdBuffer] = this.readBackPickingBufferRect(rectX, rectY, rectW, rectH);
        // Search in a spiral, starting from under the cursor, and find the closest pickable object to the cursor, if
        // there is one
        for (let i = 0; i < SPIRAL_SEQUENCE.length; i += 2) {
            const sX = SPIRAL_SEQUENCE[i + 0];
            const sY = SPIRAL_SEQUENCE[i + 1];
            const pX = x + sX;
            const pY = y + sY;
            const rX = pX - rectX;
            const rY = pY - rectY;
            if (pX >= rectX && pX < rectX + rectW && pY >= rectY && pY < rectY + rectH) {
                const rI = rX + rY * rectW;
                const id = objectIdBuffer[rI];
                const eId = extraIdBuffer[rI];
                // Top two bytes will always be set because of the background clear color (0xFFxxxxxx).
                // If we don't have at least that many unique pickable objects, there will be no
                // ID conflicts.
                if (id > 0) {
                    return {
                        position: this.decodeDepthToPosition(depthBuffer[rI * 4], pX, pY, worldSpace),
                        object: (_a = this.pickingManager.getForId(id)) !== null && _a !== void 0 ? _a : null,
                        extraId: eId,
                    };
                }
            }
        }
        const mX = x - rectX;
        const mY = y - rectY;
        const mI = mX + mY * rectW;
        const position = this.decodeDepthToPosition(depthBuffer[mI * 4], x, y, worldSpace);
        const objectId = objectIdBuffer[mI];
        const extraId = extraIdBuffer[mI];
        const object = (_b = this.pickingManager.getForId(objectId)) !== null && _b !== void 0 ? _b : null;
        return {
            position,
            object,
            extraId,
        };
    }
    /**
     *
     * @param value
     * @param x
     * @param y
     * @param worldSpace
     * @private
     */
    decodeDepthToPosition(depth, x, y, worldSpace = true) {
        const size = this.threeRenderer.getSize();
        const width = size.x;
        const height = size.y;
        // If the camera is perspective, decode the logarithmic depth buffer before unprojection
        if (this.camera.getProjectionMode() === CameraProjection.Perspective) {
            depth = MathUtils.decodeLogarithmicDepth(depth, this.camera.getNearPlane(), this.camera.getFarPlane());
        }
        // Convert screen coordinates into NDC
        const ndc = MathUtils.screenCoordsToNDC(x, y, width, height, new Vector3());
        ndc.z = depth * 2 - 1;
        // Unproject the NDC coordinate
        const inverseProjectionMatrix = new Matrix4();
        this.camera.getInverseProjectionMatrix(inverseProjectionMatrix);
        const deprojected = new Vector4(ndc.x, ndc.y, ndc.z, 1).applyMatrix4(inverseProjectionMatrix);
        deprojected.divideScalar(deprojected.w);
        const position = new Vector3(deprojected.x, deprojected.y, deprojected.z);
        if (worldSpace) {
            position.applyMatrix4(this.camera.object.matrixWorld);
        }
        return position;
    }
    /**
     * Internal function for performing a read-back of a rectangular region of the depth buffer.
     * @param x Center x of the rectangle
     * @param y Center y of the rectangle
     * @param w Width of the rectangle
     * @param h Height of the rectangle
     * @private
     */
    readBackDepthBufferRect(x, y, w, h) {
        const rbBuffer = this.targets.targets[this.targets.pickIndex].depthReadBack;
        // Read the depth buffer and decode the depth based on the camera's projection
        const buffer = new Float32Array(4 * w * h);
        this.threeRenderer.renderer.readRenderTargetPixels(rbBuffer, x, y, w, h, buffer, undefined);
        return buffer;
    }
    readBackPickingBufferRect(x, y, w, h) {
        const properties = this.threeRenderer.renderer.properties;
        const gl = this.threeRenderer.gl;
        /**
         * Internal function that implements texture read-back using raw WebGL calls (mostly). ThreeJS lacks the
         * machinery to do read-back of MRT buffers without making extra redundant framebuffer objects, so we implement
         * it manually.
         *
         * WebGL has the 'readBuffer' function for selecting which buffer in an MRT target to read from. ThreeJS doesn't
         * use this, so to avoid creating extra FBOs I just implement it like a sane individual.
         * @param num
         */
        const readBuffer = (num) => {
            // Allocate enough space for the buffer to be read into
            const buffer = new Uint8Array(4 * w * h);
            // Get the raw WebGL framebuffer object out of the target
            const fbBuffer = this.targets.targets[this.targets.pickIndex].main;
            const fb = properties.get(fbBuffer).__webglFramebuffer;
            gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fb);
            // Use the three-js state tracker to bind the framebuffer, so we don't break three-js
            this.threeRenderer.renderer.state.bindFramebuffer(gl.READ_FRAMEBUFFER, fb);
            // Perform the actual 'readPixels' with the requested buffer value
            gl.readBuffer(num);
            gl.readPixels(x, y, w, h, gl.RGBA, gl.UNSIGNED_BYTE, buffer);
            return buffer;
        };
        // Capture old READ_BUFFER state as we clobber it during these calls
        const oldReadBuffer = gl.getParameter(gl.READ_BUFFER);
        // Perform our actual read-back calls
        const objectIdBuffer = readBuffer(gl.COLOR_ATTACHMENT1);
        const extraIdBuffer = readBuffer(gl.COLOR_ATTACHMENT2);
        // Restore the old READ_BUFFER state. ThreeJS doesn't track this state, so we need to preserve it ourselves.
        gl.readBuffer(oldReadBuffer);
        const decodeIdBuffer = (src) => {
            // tslint:disable:no-bitwise
            const dst = new Array(src.length / 4);
            for (let i = 0; i < dst.length; i += 1) {
                const srcI = i * 4;
                dst[i] = (src[srcI + 0] << 0) | (src[srcI + 1] << 8) | (src[srcI + 2] << 16) | (src[srcI + 3] << 24);
            }
            return dst;
            // tslint:enable
        };
        const convertedObjectIdBuffer = decodeIdBuffer(objectIdBuffer);
        const convertedExtraIdBuffer = decodeIdBuffer(extraIdBuffer);
        const mainRt = this.targets.targets[this.targets.pickIndex].main;
        this.threeRenderer.renderer.readRenderTargetPixels(mainRt, x, y, 1, 1, new Uint8Array(4), undefined);
        return [convertedObjectIdBuffer, convertedExtraIdBuffer];
    }
    /**
     * Internal function for performing a read-back of a rectangular region of the depth buffer.
     * @param x Center x of the rectangle
     * @param y Center y of the rectangle
     * @param w Width of the rectangle
     * @param h Height of the rectangle
     * @private
     */
    calculateReadPixelsArgs(x, y, w, h) {
        // Get the size of the back buffers
        const size = this.threeRenderer.getSize();
        // Offset the input coordinates, which define the center of the rect, to the corner to follow WebGL convention
        x -= Math.trunc(w / 2);
        y -= Math.trunc(h / 2);
        // Clamp the read base to the
        const clampedX = Math.min(Math.max(x, 0), size.x - 1);
        const clampedY = Math.min(Math.max(y, 0), size.y - 1);
        // Get the difference between the offset coordinate and the clamped coordinate. We need to shrink the read-back
        // to clamp it inside the image bounds
        const clampDiffX = clampedX - x;
        const clampDiffY = clampedY - y;
        return [clampedX, clampedY, w - clampDiffX, h - clampDiffY];
    }
    createFontAsset(desc) {
        return __awaiter(this, void 0, void 0, function* () {
            const loadedFont = yield FontLoader.load(desc.url);
            const textureSize = this.threeRenderer.renderer.capabilities.maxTextureSize;
            return new FontAsset(loadedFont, Math.min(2048, textureSize / 2), 32);
        });
    }
    clearNetworkCache() {
        FileLoader.clearCache();
    }
    pickScreenWorldSpaceImpl(point) {
        const result = this.pickScreen(point.x, point.y, true);
        return Renderer.handlePickResult(result);
    }
    pickScreenViewSpaceImpl(point) {
        const result = this.pickScreen(point.x, point.y, false);
        return Renderer.handlePickResult(result);
    }
    pickScreenWorldSpaceSnappingImpl(point) {
        const result = this.pickScreenSpiral(point.x, point.y, true);
        return Renderer.handlePickResult(result);
    }
    pickScreenViewSpaceSnappingImpl(point) {
        const result = this.pickScreenSpiral(point.x, point.y, false);
        return Renderer.handlePickResult(result);
    }
}
function createRenderer(width, height) {
    // Construct the renderer instance
    const threeRenderer = new ThreeRenderer(width, height);
    const targets = new RenderTargets(threeRenderer, width, height);
    const tileCompositorPass = new TileCompositorPass();
    const passSets = [];
    for (const t of targets.targets) {
        // Renders all 'main' objects into the main buffer. This includes all opaque objects that support picking
        const mainPass = new ScenePass(t.main, {
            clearColor: true,
            clearDepth: true,
            clearStencil: true,
            sceneIndex: 0,
        });
        // Renders all transparent objects into the main buffer. Objects in this class don't support picking (at least
        // while transparent)
        const transparentPass = new ScenePass(t.noPicking, {
            clearColor: false,
            clearDepth: false,
            clearStencil: false,
            sceneIndex: 1,
        });
        // Render all KML objects into the main buffer with a separate depth buffer. Objects in this category will
        // unconditionally overlay the contents of the 'mainPass' (as long as 'kmlPass' is run after 'mainPass')
        const kmlPass = new ScenePass(t.kml, {
            clearColor: false,
            clearDepth: true,
            clearStencil: true,
            sceneIndex: 2,
        });
        // Construct the debug pass. This renders all debug objects.
        const debugPass = DebugDraw.getRenderPass(t.noPicking);
        // Render all pano objects into the main buffer. Panos will unconditionally overlay everything rendered before
        // it (by disabling depth writes/depth testing)
        const panoPass = new ScenePass(t.noPicking, {
            clearColor: false,
            clearDepth: false,
            clearStencil: false,
            sceneIndex: 3,
        });
        // A second 'mainPass' pass that will run after panoramas have been drawn. This allows rendering objects on top
        // of the panoramas while still self-occluding with the rest of main geometry
        const afterPanoPass = new ScenePass(t.main, {
            clearColor: false,
            clearDepth: false,
            clearStencil: false,
            sceneIndex: 4,
        });
        // A second 'transparentPass', to again render on top of panoramas.
        const afterPanoTransparentPass = new ScenePass(t.noPicking, {
            clearColor: false,
            clearDepth: false,
            clearStencil: false,
            sceneIndex: 5,
        });
        // A second 'kmlPass', to once again render on top of panoramas.
        const kmlAfterPanoPass = new ScenePass(t.kml, {
            clearColor: false,
            clearDepth: false,
            clearStencil: false,
            sceneIndex: 6,
        });
        // Pass that handles copying from the main render target to the canvas for display
        const mainToCanvasCopyPass = new FullscreenQuadCopyPass(t.main.texture[0], null);
        // Pass that copies the depth texture into a colour texture to allow read back for picking
        // @ts-ignore
        const readBackCopyPass = new FullscreenQuadCopyPass(t.main.depthTexture, t.depthReadBack);
        passSets.push([
            tileCompositorPass,
            mainPass,
            transparentPass,
            kmlPass,
            debugPass,
            panoPass,
            afterPanoPass,
            afterPanoTransparentPass,
            kmlAfterPanoPass,
            mainToCanvasCopyPass,
            readBackCopyPass,
        ]);
    }
    return {
        threeRenderer,
        targets,
        passSets,
        tileCompositorPass,
    };
}
function spiralSequence(base, num) {
    const sequence = new Array();
    for (let i = base; i < num; i += 1) {
        sequence.push(...ringSequence(i));
    }
    return sequence;
}
function ringSequence(layer) {
    // Special case the 0th layer.
    if (layer === 0) {
        return [0, 0];
    }
    // Number of pixels along a single side.
    const sideLength = 1 + layer * 2;
    const sequence = new Array();
    // Push the first 4 ordinals
    sequence.push(...starSequence(0, layer));
    // Get the number of pixels along a single ring edge, excluding the two corners and the middle pixel.
    const innerSideNum = sideLength - 3;
    // Loop once for each pair of pixels along the ring edge
    for (let i = 1; i <= innerSideNum / 2; i += 1) {
        sequence.push(...starSequence(i, layer));
        sequence.push(...starSequence(-i, layer));
    }
    sequence.push(...starSequence(layer, layer));
    return sequence;
}
function starSequence(x, y) {
    const sequence = new Array();
    sequence.push(x, y);
    sequence.push(y, -x);
    sequence.push(-x, -y);
    sequence.push(-y, x);
    return sequence;
}
const SPIRAL_LAYERS = 25;
const SPIRAL_W = SPIRAL_LAYERS * 2 + 1;
const SPIRAL_H = SPIRAL_LAYERS * 2 + 1;
const SPIRAL_SEQUENCE = spiralSequence(0, SPIRAL_LAYERS);
